import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  processQueuedRequests,
  cleanupOldRequests,
  getQueueStatus
} from '../common/helperFunctions/duplicatePreventionUtils';
import { createSiteReceiptCoupa, updateManifestAsnsCoupa } from '../common/helperFunctions/coupa';
import { mapToReceiptedItems } from '../pages/receive-manifest/details/detail.mapping';
import { variousAsn } from '../common/constants';

interface IsOnlineContextType {
  isOnline: boolean;
  queueStatus: {
    pendingCount: number;
    queuedCount: number;
    completedCount: number;
  };
  syncStatus: {
    isSyncing: boolean;
    syncedCount: number;
    totalToSync: number;
  };
}

const IsOnlineContext = createContext<IsOnlineContextType | undefined>(undefined);

export const useIsOnline = () => {
  const context = useContext(IsOnlineContext);
  if (context === undefined) {
    throw new Error('useIsOnline must be used within an IsOnlineProvider');
  }
  return context;
};

interface IsOnlineProviderProps {
  children: ReactNode;
}

export const IsOnlineProvider: React.FC<IsOnlineProviderProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [queueStatus, setQueueStatus] = useState({
    pendingCount: 0,
    queuedCount: 0,
    completedCount: 0
  });
  const [syncStatus, setSyncStatus] = useState({
    isSyncing: false,
    syncedCount: 0,
    totalToSync: 0
  });

  // Function to actively test network connectivity
  async function testNetworkConnectivity(): Promise<boolean> {
    try {
      // Try to fetch a small resource with a timeout and cache busting
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      // Use a cache-busting parameter to ensure we're not getting cached results
      const cacheBuster = Date.now();
      const response = await fetch(`/favicon.ico?t=${cacheBuster}`, {
        method: 'HEAD',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      // console.log('Network connectivity test response:', response.status, response.ok);
      return response.ok;
    } catch (error) {
      // console.log('Network connectivity test failed:', error);
      return false;
    }
  }

  useEffect(() => {
    async function handleComingOnline() {
      console.log('Coming back online - processing queued manifest requests');

      const currentQueue = getQueueStatus();
      if (currentQueue.queuedCount > 0) {
        setSyncStatus({
          isSyncing: true,
          syncedCount: 0,
          totalToSync: currentQueue.queuedCount
        });
      }

      try {
        let processedCount = 0;

        await processQueuedRequests(async (requestData) => {
          try {
            const { manifest, tripData, userDetails } = requestData;

            // Process the manifest submission
            const receiptedItems = mapToReceiptedItems(manifest);

            for (const receiptedItem of receiptedItems) {
              const receiptResponse = await createSiteReceiptCoupa(receiptedItem);
              if (receiptResponse.error) {
                console.error('Error receipting items to coupa:', receiptResponse.error);
                return { success: false, error: receiptResponse.error };
              }
            }

            const uniqueAsns = [...new Set(receiptedItems.map((item) => item.asn))];
            const filteredAsns = uniqueAsns.filter(
              (asn): asn is string => asn !== undefined && asn !== null && asn !== variousAsn
            );

            if (filteredAsns.length > 0) {
              await updateManifestAsnsCoupa({ asns: filteredAsns });
            }

            console.log(`Successfully processed queued manifest ${manifest.id}`);

            // Update sync progress
            processedCount++;
            setSyncStatus((prev) => ({
              ...prev,
              syncedCount: processedCount
            }));

            return { success: true };
          } catch (error) {
            console.error('Error processing queued manifest request:', error);
            return { success: false, error: String(error) };
          }
        });

        // Clean up old requests after processing
        cleanupOldRequests();

        // Update queue status after processing
        setQueueStatus(getQueueStatus());

        // Show success feedback and reset sync status
        if (processedCount > 0) {
          console.log(`✓ Successfully synced ${processedCount} submission${processedCount !== 1 ? 's' : ''}`);

          // Show success state briefly before resetting
          setSyncStatus({
            isSyncing: false,
            syncedCount: processedCount,
            totalToSync: processedCount
          });

          // Reset sync status after showing success
          setTimeout(() => {
            setSyncStatus({
              isSyncing: false,
              syncedCount: 0,
              totalToSync: 0
            });
          }, 3000);
        }
      } catch (error) {
        console.error('Error processing queued requests:', error);
        setSyncStatus({
          isSyncing: false,
          syncedCount: 0,
          totalToSync: 0
        });
      }
    }

    function changeStatus() {
      const newOnlineStatus = navigator.onLine;
      console.log('Network status changed:', newOnlineStatus ? 'online' : 'offline');

      setIsOnline((prevIsOnline) => {
        // When coming back online, process queued requests
        if (newOnlineStatus && !prevIsOnline) {
          handleComingOnline();
        }
        return newOnlineStatus;
      });

      // Update queue status
      setQueueStatus(getQueueStatus());
    }

    // Periodic network connectivity check
    async function periodicConnectivityCheck() {
      // console.log('Running periodic connectivity check...');
      const isActuallyOnline = await testNetworkConnectivity();
      const browserOnlineStatus = navigator.onLine;

      // console.log(
      //   'Periodic connectivity check - Browser status:',
      //   browserOnlineStatus,
      //   'Actual connectivity:',
      //   isActuallyOnline,
      //   'Current state:',
      //   isOnline
      // );

      setIsOnline((prevIsOnline) => {
        // console.log('Previous online state:', prevIsOnline);

        // If we can't actually connect, set to offline regardless of browser status
        if (!isActuallyOnline) {
          console.log('No actual connectivity detected - setting offline');
          return false;
        }
        // If we can connect and browser agrees, set to online
        else if (isActuallyOnline && browserOnlineStatus) {
          if (!prevIsOnline) {
            console.log('Connectivity restored - setting online');
            handleComingOnline();
          }
          return true;
        }
        // If we can connect but browser says offline, trust our test
        else if (isActuallyOnline && !browserOnlineStatus) {
          console.log('Browser reports offline but connectivity detected - setting online');
          if (!prevIsOnline) {
            handleComingOnline();
          }
          return true;
        }

        return prevIsOnline;
      });

      // Update queue status
      setQueueStatus(getQueueStatus());
    }

    window.addEventListener('online', changeStatus);
    window.addEventListener('offline', changeStatus);
    changeStatus();

    // Set up periodic connectivity checking every 10 seconds for testing
    const connectivityInterval = setInterval(periodicConnectivityCheck, 10000);

    // Run initial connectivity check after a short delay
    setTimeout(periodicConnectivityCheck, 2000);

    // Clean up old requests on initial load
    cleanupOldRequests();
    setQueueStatus(getQueueStatus());

    return () => {
      window.removeEventListener('online', changeStatus);
      window.removeEventListener('offline', changeStatus);
      clearInterval(connectivityInterval);
    };
  }, []); // Remove isOnline dependency to prevent effect from re-running

  return <IsOnlineContext.Provider value={{ isOnline, queueStatus, syncStatus }}>{children}</IsOnlineContext.Provider>;
};
